import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Package, LogOut, Wallet, Truck, History, Calculator,
  Plus, Building, ChevronDown, Menu, X,
  MessageSquare, Scale, AlertTriangle, Send, CheckCircle, MapPin
} from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import QuoteResults from "@/components/QuoteResults";
import ShipmentsTable from "@/components/ShipmentsTable";
import TransactionsTable from "@/components/TransactionsTable";
import AddressBook from "@/components/AddressBook";
import TrackingTimeline from "@/components/TrackingTimeline";

const Dashboard = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const { user, logout, updateBalance } = useAuth();
  const [activeTab, setActiveTab] = useState(tab || "cotizar");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Quote state
  const [quoteForm, setQuoteForm] = useState({
    origin_zip: "",
    destination_zip: "",
    weight: "",
    length: "",
    width: "",
    height: ""
  });
  const [quotes, setQuotes] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Recharge state
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [rechargeResult, setRechargeResult] = useState(null);
  const [userRecharges, setUserRecharges] = useState([]);

  // Data state
  const [shipments, setShipments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [sobrepesos, setSobrepesos] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Ticket form state
  const [ticketForm, setTicketForm] = useState({ subject: "", message: "", category: "soporte" });
  const [ticketLoading, setTicketLoading] = useState(false);
  const [trackingShipment, setTrackingShipment] = useState(null);

  // Colonia lookup state for quote form
  const [originColonias, setOriginColonias] = useState(null);
  const [destColonias, setDestColonias] = useState(null);
  const [selectedOriginColonia, setSelectedOriginColonia] = useState("");
  const [selectedDestColonia, setSelectedDestColonia] = useState("");

  const lookupZipDash = async (zip, setter) => {
    if (zip.length === 5) {
      try {
        const res = await axios.get(`${API}/zipcode/${zip}`);
        if (res.data.colonias?.length > 0) setter(res.data);
        else setter(null);
      } catch { setter(null); }
    } else { setter(null); }
  };

  const handleOriginZipDash = (val) => {
    setQuoteForm(f => ({...f, origin_zip: val}));
    setSelectedOriginColonia("");
    lookupZipDash(val, setOriginColonias);
  };

  const handleDestZipDash = (val) => {
    setQuoteForm(f => ({...f, destination_zip: val}));
    setSelectedDestColonia("");
    lookupZipDash(val, setDestColonias);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (tab) setActiveTab(tab);
  }, [tab]);

  const fetchData = async () => {
    try {
      const [shipmentsRes, transactionsRes, ticketsRes, sobrepesosRes, rechargesRes] = await Promise.all([
        axios.get(`${API}/shipments`),
        axios.get(`${API}/transactions`),
        axios.get(`${API}/tickets`),
        axios.get(`${API}/sobrepesos`),
        axios.get(`${API}/recharges`),
      ]);
      setShipments(shipmentsRes.data.shipments || []);
      setTransactions(transactionsRes.data.transactions || []);
      setTickets(ticketsRes.data.tickets || []);
      setSobrepesos(sobrepesosRes.data.sobrepesos || []);
      setUserRecharges(rechargesRes.data.recharges || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleQuote = async (e) => {
    e.preventDefault();
    setQuoteLoading(true);
    setQuotes(null);
    
    try {
      const headers = {};
      const token = localStorage.getItem("token");
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await axios.post(`${API}/quotes/calculate`, {
        origin_zip: quoteForm.origin_zip,
        destination_zip: quoteForm.destination_zip,
        weight: parseFloat(quoteForm.weight),
        length: parseFloat(quoteForm.length),
        width: parseFloat(quoteForm.width),
        height: parseFloat(quoteForm.height)
      }, { headers });
      setQuotes(response.data);
    } catch (error) {
      toast.error("Error al cotizar");
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleCreateShipment = async (quote) => {
    // Navigate to full shipping flow with quote data
    navigate("/enviar", {
      state: {
        quote: quote,
        quoteDetails: quotes
      }
    });
  };

  const handleRecharge = async (e) => {
    e.preventDefault();
    const amount = parseFloat(rechargeAmount);
    
    if (amount < 100) {
      toast.error("El monto mínimo de recarga es $100 MXN");
      return;
    }

    setRechargeLoading(true);
    setRechargeResult(null);

    try {
      const response = await axios.post(`${API}/recharges/spei`, { amount });
      setRechargeResult(response.data);
      toast.success("Solicitud de recarga creada. Realiza tu transferencia SPEI.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar recarga");
    } finally {
      setRechargeLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setTicketLoading(true);

    try {
      await axios.post(`${API}/tickets`, ticketForm);
      toast.success("Ticket creado exitosamente");
      setTicketForm({ subject: "", message: "", category: "soporte" });
      fetchData();
    } catch (error) {
      toast.error("Error al crear ticket");
    } finally {
      setTicketLoading(false);
    }
  };

  const handlePaySobrepeso = async (sobrepeso) => {
    if (user.balance < sobrepeso.extra_charge) {
      toast.error("Saldo insuficiente para pagar el sobrepeso");
      setActiveTab("recargar");
      return;
    }

    try {
      await axios.post(`${API}/sobrepesos/${sobrepeso.sobrepeso_id}/pay`);
      toast.success("Sobrepeso pagado exitosamente");
      updateBalance(user.balance - sobrepeso.extra_charge);
      fetchData();
    } catch (error) {
      toast.error("Error al pagar sobrepeso");
    }
  };

  const handleDisputeSobrepeso = async (sobrepeso) => {
    const reason = prompt("Describe el motivo de tu disputa:");
    if (!reason) return;

    try {
      await axios.post(`${API}/sobrepesos/${sobrepeso.sobrepeso_id}/dispute`, null, {
        params: { reason }
      });
      toast.success("Disputa creada. Te contactaremos pronto.");
      fetchData();
    } catch (error) {
      toast.error("Error al crear disputa");
    }
  };

  const presetAmounts = [100, 250, 500, 1000, 2500, 5000];

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
              <Package className="w-8 h-8 text-violet-600" />
              <span className="text-2xl font-bold font-['Outfit'] gradient-text">SuperPack</span>
            </div>

            {/* Desktop Balance & User */}
            <div className="hidden md:flex items-center gap-4">
              {user?.role === "admin" && (
                <Button
                  variant="outline"
                  onClick={() => navigate("/admin")}
                  className="border-violet-600 text-violet-600 hover:bg-violet-50"
                  data-testid="admin-panel-btn"
                >
                  Panel Admin
                </Button>
              )}
              <Card className="bg-violet-50 border-violet-200">
                <CardContent className="py-2 px-4 flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-violet-600" />
                  <div>
                    <p className="text-xs text-violet-600">Saldo disponible</p>
                    <p className="font-bold text-violet-700" data-testid="user-balance">
                      ${user?.balance?.toFixed(2) || "0.00"} MXN
                    </p>
                  </div>
                </CardContent>
              </Card>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-violet-600 text-white">
                        {user?.name?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden lg:inline">{user?.name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem className="text-zinc-600">
                    {user?.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-zinc-200 py-4 px-4">
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="w-10 h-10">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-violet-600 text-white">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-zinc-500">{user?.email}</p>
              </div>
            </div>
            <Card className="bg-violet-50 border-violet-200 mb-4">
              <CardContent className="py-2 px-4 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-violet-600" />
                <div>
                  <p className="text-xs text-violet-600">Saldo disponible</p>
                  <p className="font-bold text-violet-700">${user?.balance?.toFixed(2) || "0.00"} MXN</p>
                </div>
              </CardContent>
            </Card>
            <Button variant="outline" onClick={handleLogout} className="w-full text-red-600">
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </Button>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Mobile-friendly tabs with visible labels */}
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-max sm:w-auto gap-1 bg-zinc-100 p-1 rounded-xl">
              <TabsTrigger value="cotizar" className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-cotizar">
                <Calculator className="w-4 h-4" />
                Cotizar
              </TabsTrigger>
              <TabsTrigger value="direcciones" className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-direcciones">
                <MapPin className="w-4 h-4" />
                Direcciones
              </TabsTrigger>
              <TabsTrigger value="historial" className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-historial">
                <History className="w-4 h-4" />
                Envíos
              </TabsTrigger>
              <TabsTrigger value="recargar" className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-recargar">
                <Wallet className="w-4 h-4" />
                Recargar
              </TabsTrigger>
              <TabsTrigger value="tickets" className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-tickets">
                <MessageSquare className="w-4 h-4" />
                Soporte
              </TabsTrigger>
              <TabsTrigger value="sobrepesos" className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap relative" data-testid="tab-sobrepesos">
                <Scale className="w-4 h-4" />
                Sobrepesos
                {sobrepesos.filter(s => s.status === "pendiente").length > 0 && (
                  <span className="w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center ml-1">
                    {sobrepesos.filter(s => s.status === "pendiente").length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Quote Tab */}
          <TabsContent value="cotizar" className="space-y-6">
            {/* Nuevo Envío CTA */}
            <Card className="bg-gradient-to-r from-violet-600 to-purple-600 text-white border-0 shadow-xl shadow-violet-500/20">
              <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Send className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Nuevo Envío</h3>
                    <p className="text-violet-200 text-sm">Cotiza, elige paquetería y genera tu guía al instante</p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate("/enviar")}
                  className="bg-white text-violet-700 hover:bg-violet-50 font-bold px-6 py-5 w-full sm:w-auto"
                  data-testid="new-shipment-cta-btn"
                >
                  <Truck className="w-4 h-4 mr-2" /> Crear envío
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-violet-600" />
                  Cotiza tu envío
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleQuote} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Origin & Destination */}
                    <div className="space-y-4">
                      <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        Origen y Destino
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>CP Origen</Label>
                          <Input
                            placeholder="06600"
                            value={quoteForm.origin_zip}
                            onChange={(e) => handleOriginZipDash(e.target.value)}
                            required
                            maxLength={5}
                            className="h-11"
                            data-testid="dash-origin-zip"
                          />
                        </div>
                        <div>
                          <Label>CP Destino</Label>
                          <Input
                            placeholder="44100"
                            value={quoteForm.destination_zip}
                            onChange={(e) => handleDestZipDash(e.target.value)}
                            required
                            maxLength={5}
                            className="h-11"
                            data-testid="dash-destination-zip"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Package Details */}
                    <div className="space-y-4">
                      <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        Datos del paquete
                      </h3>
                      <div>
                        <Label>Peso (kg)</Label>
                        <Input
                          type="number"
                          placeholder="2.5"
                          value={quoteForm.weight}
                          onChange={(e) => setQuoteForm({...quoteForm, weight: e.target.value})}
                          required
                          min="0.1"
                          step="0.1"
                          data-testid="dash-weight"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label>Largo (cm)</Label>
                          <Input
                            type="number"
                            placeholder="30"
                            value={quoteForm.length}
                            onChange={(e) => setQuoteForm({...quoteForm, length: e.target.value})}
                            required
                            min="1"
                            data-testid="dash-length"
                          />
                        </div>
                        <div>
                          <Label>Ancho (cm)</Label>
                          <Input
                            type="number"
                            placeholder="20"
                            value={quoteForm.width}
                            onChange={(e) => setQuoteForm({...quoteForm, width: e.target.value})}
                            required
                            min="1"
                            data-testid="dash-width"
                          />
                        </div>
                        <div>
                          <Label>Alto (cm)</Label>
                          <Input
                            type="number"
                            placeholder="15"
                            value={quoteForm.height}
                            onChange={(e) => setQuoteForm({...quoteForm, height: e.target.value})}
                            required
                            min="1"
                            data-testid="dash-height"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full md:w-auto btn-primary px-8 py-6"
                    disabled={quoteLoading}
                    data-testid="dash-quote-btn"
                  >
                    {quoteLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Cotizando...
                      </div>
                    ) : (
                      "Cotizar envío"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Quote Results */}
            {quotes && (
              <QuoteResults 
                quotes={quotes} 
                showActions={true} 
                onSelect={handleCreateShipment}
              />
            )}
          </TabsContent>

          {/* Direcciones Tab */}
          <TabsContent value="direcciones" className="space-y-6">
            <AddressBook />
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="historial" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-violet-600" />
                    Envíos recientes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ShipmentsTable shipments={shipments} loading={dataLoading} onTrack={setTrackingShipment} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-violet-600" />
                    Movimientos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <TransactionsTable transactions={transactions} loading={dataLoading} />
                </CardContent>
              </Card>
            </div>

            {trackingShipment && (
              <TrackingTimeline shipmentId={trackingShipment} />
            )}
          </TabsContent>

          {/* Recharge Tab - SPEI Only */}
          <TabsContent value="recargar" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* SPEI Instructions & Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-violet-600" />
                    Recargar saldo por SPEI
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* SPEI Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6" data-testid="spei-info-box">
                    <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                      <Building className="w-5 h-5" />
                      Datos para transferencia SPEI
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center bg-white rounded-lg p-3">
                        <span className="text-zinc-500">Banco:</span>
                        <span className="font-bold text-blue-800" data-testid="spei-bank">STP</span>
                      </div>
                      <div className="flex justify-between items-center bg-white rounded-lg p-3">
                        <span className="text-zinc-500">CLABE:</span>
                        <span className="font-mono font-bold text-blue-800 text-base" data-testid="spei-clabe">721180100000891868</span>
                      </div>
                      <div className="flex justify-between items-center bg-white rounded-lg p-3">
                        <span className="text-zinc-500">Beneficiario:</span>
                        <span className="font-bold text-blue-800">SUPERPACK</span>
                      </div>
                      <div className="flex justify-between items-center bg-white rounded-lg p-3">
                        <span className="text-zinc-500">Concepto/Referencia:</span>
                        <span className="font-medium text-blue-800">{user?.email}</span>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-3">
                      La recarga se acreditará en ~15 minutos después de la aprobación. Usa tu correo registrado como concepto/referencia.
                    </p>
                  </div>

                  <form onSubmit={handleRecharge} className="space-y-5">
                    {/* Preset amounts */}
                    <div>
                      <Label>Selecciona un monto</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {presetAmounts.map((amount) => (
                          <Button
                            key={amount}
                            type="button"
                            variant={rechargeAmount === String(amount) ? "default" : "outline"}
                            className={rechargeAmount === String(amount) ? "btn-primary" : ""}
                            onClick={() => setRechargeAmount(String(amount))}
                            data-testid={`preset-amount-${amount}`}
                          >
                            ${amount}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Custom amount */}
                    <div>
                      <Label>O ingresa otro monto</Label>
                      <Input
                        type="number"
                        placeholder="Monto en MXN"
                        value={rechargeAmount}
                        onChange={(e) => setRechargeAmount(e.target.value)}
                        min="100"
                        className="mt-2"
                        data-testid="custom-amount-input"
                      />
                      <p className="text-xs text-zinc-500 mt-1">Mínimo $100 MXN - Sin comisión</p>
                    </div>

                    {/* Summary */}
                    {rechargeAmount && parseFloat(rechargeAmount) >= 100 && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-600">Monto a transferir</span>
                          <span className="font-bold text-emerald-700">${parseFloat(rechargeAmount).toFixed(2)} MXN</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span className="text-zinc-600">Comisión</span>
                          <span className="font-medium text-emerald-600">$0.00 MXN</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1 pt-2 border-t border-emerald-200 font-bold">
                          <span>Se acreditará a tu saldo</span>
                          <span className="text-emerald-700">${parseFloat(rechargeAmount).toFixed(2)} MXN</span>
                        </div>
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full btn-primary py-6"
                      disabled={rechargeLoading || !rechargeAmount || parseFloat(rechargeAmount) < 100}
                      data-testid="recharge-submit-btn"
                    >
                      {rechargeLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Procesando...
                        </div>
                      ) : rechargeAmount && parseFloat(rechargeAmount) >= 100 ? (
                        `Solicitar recarga de $${parseFloat(rechargeAmount).toFixed(2)} MXN`
                      ) : (
                        "Solicitar recarga (mín. $100)"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Right: Result or Recharge History */}
              <div className="space-y-6">
                {rechargeResult && (
                  <Card className="border-blue-500">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                        Solicitud creada
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                        <p className="text-blue-800 text-sm">{rechargeResult.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recharge History */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5 text-violet-600" />
                      Historial de recargas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {userRecharges.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        <Wallet className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
                        <p>Aún no has solicitado recargas</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {userRecharges.map((rch) => (
                          <div key={rch.recharge_id} className="p-3 border border-zinc-200 rounded-lg" data-testid={`recharge-${rch.recharge_id}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-bold text-lg">${rch.amount?.toFixed(2)} MXN</p>
                                <p className="text-xs text-zinc-500">
                                  {new Date(rch.created_at).toLocaleDateString("es-MX", {
                                    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                                  })}
                                </p>
                              </div>
                              <Badge className={
                                rch.status === "pending" ? "bg-amber-100 text-amber-700" :
                                rch.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                                "bg-red-100 text-red-700"
                              }>
                                {rch.status === "pending" ? "Pendiente" :
                                 rch.status === "approved" ? "Aprobada" : "Rechazada"}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Sobrepesos Tab */}
          <TabsContent value="sobrepesos" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-violet-600" />
                  Sobrepesos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sobrepesos.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
                    <p className="text-lg font-medium">¡Sin sobrepesos!</p>
                    <p className="text-sm">No tienes cobros pendientes por sobrepeso.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sobrepesos.map((sobrepeso) => (
                      <Card key={sobrepeso.sobrepeso_id} className={`${
                        sobrepeso.status === "pendiente" ? "border-amber-500" : 
                        sobrepeso.status === "pagado" ? "border-emerald-500" : "border-blue-500"
                      }`}>
                        <CardContent className="p-4">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className={`${
                                  sobrepeso.status === "pendiente" ? "bg-amber-100 text-amber-700" :
                                  sobrepeso.status === "pagado" ? "bg-emerald-100 text-emerald-700" :
                                  "bg-blue-100 text-blue-700"
                                }`}>
                                  {sobrepeso.status === "pendiente" ? "Pendiente" :
                                   sobrepeso.status === "pagado" ? "Pagado" : "En disputa"}
                                </Badge>
                                <span className="font-mono text-sm text-zinc-500">{sobrepeso.tracking_number}</span>
                              </div>
                              <p className="font-medium">{sobrepeso.carrier}</p>
                              <div className="text-sm text-zinc-500 mt-1">
                                <span>Declarado: {sobrepeso.declared_weight}kg</span>
                                <span className="mx-2">→</span>
                                <span className="text-red-600">Real: {sobrepeso.actual_weight}kg</span>
                                <span className="mx-2">|</span>
                                <span>Diferencia: +{sobrepeso.difference}kg</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-red-600">${sobrepeso.extra_charge.toFixed(2)}</p>
                              <p className="text-xs text-zinc-500">Cargo adicional</p>
                              {sobrepeso.status === "pendiente" && (
                                <div className="flex gap-2 mt-2">
                                  <Button 
                                    size="sm" 
                                    onClick={() => handlePaySobrepeso(sobrepeso)}
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                  >
                                    Pagar
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleDisputeSobrepeso(sobrepeso)}
                                  >
                                    Disputar
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Create Ticket */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-violet-600" />
                    Crear ticket de soporte
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateTicket} className="space-y-4">
                    <div>
                      <Label>Categoría</Label>
                      <select
                        className="w-full mt-1 p-2 border border-zinc-200 rounded-md"
                        value={ticketForm.category}
                        onChange={(e) => setTicketForm({...ticketForm, category: e.target.value})}
                      >
                        <option value="soporte">Soporte técnico</option>
                        <option value="facturacion">Facturación</option>
                        <option value="sobrepeso">Disputa de sobrepeso</option>
                        <option value="general">Consulta general</option>
                      </select>
                    </div>
                    <div>
                      <Label>Asunto</Label>
                      <Input
                        placeholder="¿En qué podemos ayudarte?"
                        value={ticketForm.subject}
                        onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Mensaje</Label>
                      <Textarea
                        placeholder="Describe tu consulta..."
                        value={ticketForm.message}
                        onChange={(e) => setTicketForm({...ticketForm, message: e.target.value})}
                        required
                        rows={4}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-violet-600 hover:bg-violet-700"
                      disabled={ticketLoading}
                    >
                      {ticketLoading ? "Enviando..." : "Enviar ticket"}
                      <Send className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* My Tickets */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-violet-600" />
                    Mis tickets
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {tickets.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
                      <p>No tienes tickets aún</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {tickets.map((ticket) => (
                        <div key={ticket.ticket_id} className="p-3 border border-zinc-200 rounded-lg hover:bg-zinc-50">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{ticket.subject}</p>
                              <p className="text-xs text-zinc-500 mt-1">
                                {new Date(ticket.created_at).toLocaleDateString("es-MX")}
                              </p>
                            </div>
                            <Badge className={`flex-shrink-0 ${
                              ticket.status === "abierto" ? "bg-amber-100 text-amber-700" :
                              ticket.status === "en_proceso" ? "bg-blue-100 text-blue-700" :
                              ticket.status === "resuelto" ? "bg-emerald-100 text-emerald-700" :
                              "bg-zinc-100 text-zinc-700"
                            }`}>
                              {ticket.status === "abierto" ? "Abierto" :
                               ticket.status === "en_proceso" ? "En proceso" :
                               ticket.status === "resuelto" ? "Resuelto" : "Cerrado"}
                            </Badge>
                          </div>
                          {ticket.responses && ticket.responses.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-zinc-100">
                              <p className="text-xs text-zinc-500">
                                {ticket.responses.length} respuesta(s)
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Dashboard;
